import { createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GlobalState } from '@store'
import { Doc, ExtraField, LoadingState, Role, Stages, User, TrackingRows } from '@types'
import { SuccessForm } from '../modals/EditStagesModal'

const defaultUser: User = {
  name: '',
  phone: '',
  city: '',
  files: [],
  document: '',
  email: '',
  role: Role.referenciador,
  isVerified: false,
  isSaver: false
}

interface trackingState {
  loadingStatus: LoadingState
  message: string
  personalData: User[]
  teamData: User[]
  executives: User[]
  userProfile: any
  viewingUser: string
  isTeamView: boolean
}

const initialState: trackingState = {
  loadingStatus: 'NOT_STARTED',
  message: '',
  personalData: [],
  teamData: [],
  executives: [],
  userProfile: {},
  viewingUser: '',
  isTeamView: false
}

export type trackingAction = PayloadAction<User>
export type patchStagesAction = PayloadAction<{
  stages: Stages
  isRejected: boolean
  isReturned: boolean
  userId: string
  isTeam: boolean
  successForm: SuccessForm
  extraFields: ExtraField[]
  hasChangedSuccess: boolean
}>
export type patchUserAction = PayloadAction<{
  dataProfile: User
  isTeam: boolean
}>
export type getUserProfileAction = PayloadAction<{
  userId: string
}>
export type deleteUserProfileAction = PayloadAction<{
  userId: string
  isTeam: boolean
}>
export type patchFilesAction = PayloadAction<{
  files: Doc[]
  userId: string
}>
export type deleteFilesAction = PayloadAction<{
  fileId: string
  userId: string
}>
export type postNewConversationAction = PayloadAction<{
  value: string
  userId?: string
  type?: string
}>
export type recoverRejectedCaseAction = PayloadAction<{
  userId: string
  isTeam: boolean
  stages: Stages
  recoveryMessage: string
}>

const trackingSlice = createSlice({
  name: 'tracking',
  initialState,

  reducers: {
    getExecutives: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getExecutivesSuccess: (state, { payload }: PayloadAction<{ data: User[]; setSuccess: boolean }>) => {
      return {
        ...state,
        loadingStatus: payload.setSuccess ? 'SUCCESS' : 'FAILED',
        executives: payload.data
      }
    },
    getComplianceAction: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getTeamTracking: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getExecutiveCases: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getPersonalTracking: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getTrackingSuccess: (state, { payload }: PayloadAction<{ data: User[]; setSuccess: boolean }>) => {
      return {
        ...state,
        loadingStatus: payload.setSuccess ? 'SUCCESS' : state.loadingStatus,
        personalData: payload.data,
        isTeamView: false
      }
    },
    getTeamTrackingSuccess: (state, action: PayloadAction<User[]>) => {
      return {
        ...state,
        loadingStatus: 'SUCCESS',
        teamData: action.payload,
        isTeamView: true
      }
    },
    getTrackingFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        message: action.payload
      }
    },
    getUserProfile: (state, { payload }: getUserProfileAction) => {
      const user = state.teamData.find(user => user._id === payload.userId)
      return {
        ...state,
        userProfile: user
      }
    },
    deleteUserProfile: (state, _action: deleteUserProfileAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getUserProfileSucces: (state, action: PayloadAction<User>) => {
      return {
        ...state,
        loadingStatus: 'SUCCESS',
        userProfile: action.payload
      }
    },
    getUserProfileFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        message: action.payload
      }
    },
    patchStages: (state, _action: patchStagesAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    patchUser: (state, _action: patchUserAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    patchFiles: (state, _action: patchFilesAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    deleteUserSuccess: (state, _action) => {
      return {
        ...state,
        loadingStatus: 'SUCCESS'
      }
    },
    deleteUserFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        message: action.payload
      }
    },
    deleteFile: (state, _action: deleteFilesAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    setViewingUser: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        viewingUser: action.payload
      }
    },
    sendSuccess: (state, _action: PayloadAction<string>) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    postNewConversation: (state, _action: postNewConversationAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    postNewConversationAndRefresh: (state, _action: postNewConversationAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    recoverRejectedCase: (state, _action: recoverRejectedCaseAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    postNewConversationSuccess: state => {
      return {
        ...state,
        loadingStatus: 'SUCCESS'
      }
    },
    postNewConversationFailed: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        message: action.payload
      }
    },
    clearTracking: _state => {
      return initialState
    }
  }
})

export const trackingLoader = createDraftSafeSelector(
  [(state: GlobalState) => state.tracking.loadingStatus, (state: GlobalState) => state.tracking.message],
  (loadingStatus, message) => {
    const isLoading = loadingStatus === 'LOADING'
    const isFailed = loadingStatus === 'FAILED'
    const isSuccess = loadingStatus === 'SUCCESS'
    return { isLoading, isFailed, isSuccess, message }
  }
)

export const selectAllSavers = createDraftSafeSelector(
  [
    (state: GlobalState): User[] => state.tracking.personalData,
    (state: GlobalState): User[] => state.tracking.teamData
  ],
  (persons, team): User[] => [...persons, ...team]
)

const calculateProgress = (stages: Stages) => {
  let progress = 0
  Object.keys(stages).forEach(key => {
    const length = stages[key as keyof Stages]?.toString().length || 0
    if (!['_id', 'feesOrPayments'].includes(key) && length > 1) {
      progress += 1
    }
  })
  return progress
}

export const selectPersonalTracking = createDraftSafeSelector(
  [(state: GlobalState): User[] => state.tracking.personalData],
  (personalTrack): TrackingRows[] => {
    const rows: TrackingRows[] = personalTrack.map(person => ({
      ...person,
      id: person._id,
      parentName: person.parentName ? person.parentName : '',
      bankName: person.bank?.toUpperCase(),
      progress: person.stages ? calculateProgress(person.stages) : 0,
      studyDate: person.stages?.studyDate || '',
      office: person.stages?.office || '',
      bank: person.bankStages?.juridicalStages?.[0]?.value || person.stages?.bank || '',
      bankDocument: person.bankDocument ?? '',
      success: person.stages?.success || '',
      feesOrPayments: person.stages?.feesOrPayments || '',
      comments: person.comments || '',
      isRejected: person?.isRejected || false,
      createdAt: person.createdAt || '2020',
      fee: person.stages?.fee ?? 0,
      payments: person.stages?.payments ?? 0,
      received: person.stages?.received ?? '',
      returned: person.stages?.returned ?? '',
      delivered: person.stages?.delivered ?? '',
      rejected: person.stages?.rejected ?? ''
    }))

    return rows
  }
)

export const selectTeamTracking = createDraftSafeSelector(
  [(state: GlobalState): User[] => state.tracking.teamData],
  (teamData): TrackingRows[] => {
    const rows: TrackingRows[] = teamData.map(person => ({
      ...person,
      id: person._id,
      parentName: person.parentName ? person.parentName : '',
      bankName: person.bank?.toUpperCase(),
      progress: person.stages ? calculateProgress(person.stages) : 0,
      studyDate: person.stages?.studyDate || '',
      office: person.stages?.office || '',
      bank: person.bankStages?.juridicalStages?.[0]?.value || person.stages?.bank || '',
      bankDocument: person.bankDocument ?? '',
      success: person.stages?.success || '',
      feesOrPayments: person.stages?.feesOrPayments || '',
      comments: person.comments || '',
      isRejected: person?.isRejected === true || (person?.isRejected as any) === 'true',
      isReturned: person?.isReturned === true || (person?.isReturned as any) === 'true',
      createdAt: person.createdAt || '2020',
      fee: person.stages?.fee ?? 0,
      payments: person.stages?.payments ?? 0,
      received: person.stages?.received ?? '',
      returned: person.stages?.returned ?? '',
      delivered: person.stages?.delivered ?? '',
      rejected: person.stages?.rejected ?? ''
    }))

    return rows
  }
)

export const selectCommissions = createDraftSafeSelector(
  [(state: GlobalState): User[] => state.tracking.teamData],
  (teamData): TrackingRows[] => {
    const rows: TrackingRows[] = teamData.map(person => ({
      ...person,
      id: person._id,
      parentName: person.parentName ? person.parentName : '',
      bankName: person.bank?.toUpperCase(),
      progress: person.stages ? calculateProgress(person.stages) : 0,
      studyDate: person.stages?.studyDate || '',
      office: person.stages?.office || '',
      bank: person.bankStages?.juridicalStages?.[0]?.value || person.stages?.bank || '',
      bankDocument: person.bankDocument ?? '',
      success: person.stages?.success || '',
      feesOrPayments: person.stages?.feesOrPayments || '',
      comments: person.comments || '',
      isRejected: person?.isRejected || false,
      createdAt: person.createdAt || '2020',
      fee: person.stages?.fee ?? 0,
      payments: person.stages?.payments ?? 0,
      received: person.stages?.received ?? '',
      returned: person.stages?.returned ?? '',
      delivered: person.stages?.delivered ?? '',
      rejected: person.stages?.rejected ?? ''
    }))

    return rows.filter(row => !row.isRejected)
  }
)

export const selectUserProfile = createDraftSafeSelector(
  [(state: GlobalState): User => state.tracking.userProfile],
  (userProfile): User => userProfile
)

export const selectViewingProfile = createDraftSafeSelector(
  [
    (
      state: GlobalState
    ): {
      personalData: User[]
      teamData: User[]
      selectedUser: string
      isTeamView: boolean
    } => ({
      personalData: state.tracking.personalData,
      teamData: state.tracking.teamData,
      selectedUser: state.tracking.viewingUser,
      isTeamView: state.tracking.isTeamView
    })
  ],
  ({ personalData, teamData, selectedUser, isTeamView }): User => {
    const data = isTeamView ? teamData : personalData
    return data.find(item => item._id === selectedUser) ?? defaultUser
  }
)

export const selectTrackingLoader = createDraftSafeSelector(
  [(state: GlobalState) => state.tracking.loadingStatus],
  loadingStatus => loadingStatus === 'LOADING'
)

export const executivesSelector = createDraftSafeSelector(
  [(state: GlobalState) => state.tracking.executives],
  executives => executives
)

export const {
  getPersonalTracking,
  getTrackingSuccess,
  getTeamTrackingSuccess,
  getTrackingFailed,
  patchStages,
  patchUser,
  patchFiles,
  deleteFile,
  clearTracking,
  getUserProfile,
  deleteUserProfile,
  deleteUserFailed,
  getUserProfileFailed,
  getUserProfileSucces,
  deleteUserSuccess,
  setViewingUser,
  sendSuccess,
  getExecutives,
  getExecutivesSuccess,
  getTeamTracking,
  getExecutiveCases,
  postNewConversation,
  postNewConversationSuccess,
  postNewConversationFailed,
  postNewConversationAndRefresh,
  recoverRejectedCase,
  getComplianceAction
} = trackingSlice.actions
export default trackingSlice.reducer
